import { useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  fromCents,
  type LedgerCycle,
  type StatementListResponse,
  type SubjectStatementResponse,
  toCents,
} from '@k-management/shared';
import { Link, useParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { Icon } from '../../components/Icon.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { StatementView } from './StatementView.js';
import { useSessionValue } from '../../lib/preferences.js';

type Sort = 'residual' | 'overdue' | 'opening' | 'name';

/**
 * Tesoreria › Schede contabili (09/10/2026): every customer and supplier
 * with invoices or a balance at 1 January, with what is open and overdue.
 */
export function StatementsPage() {
  usePageTitle('Schede contabili');
  const list = useQuery({
    queryKey: ['treasury', 'statements'],
    queryFn: () => apiRequest<StatementListResponse>('/treasury/statements'),
  });
  const [cycle, setCycle] = useSessionValue<LedgerCycle>('list.statements.cycle', 'passivo');
  const [query, setQuery] = useSessionValue('list.statements.query', '');
  const [onlyOpen, setOnlyOpen] = useSessionValue('list.statements.onlyOpen', true);
  const [sort, setSort] = useSessionValue<Sort>('list.statements.sort', 'residual');
  const needle = query.trim().toLowerCase();
  const ofCycle = (list.data?.items ?? []).filter((item) => item.cycle === cycle);
  const rows = ofCycle
    .filter(
      (item) =>
        (!onlyOpen ||
          toCents(item.residual) !== 0 ||
          (item.opening !== null && toCents(item.opening) !== 0)) &&
        (!needle || [item.name, ...(item.aliases ?? [])].join(' ').toLowerCase().includes(needle)),
    )
    .sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name, 'it')
        : toCents(b[sort] ?? '0') - toCents(a[sort] ?? '0') || a.name.localeCompare(b.name, 'it'),
    );
  // On top of the order chosen in «Ordina», which comes back on the third click.
  const sorting = useTableSort('statements', rows, {
    name: (item) => item.name,
    opening: (item) => amountSort(item.opening),
    invoices: (item) => item.invoices,
    residual: (item) => amountSort(item.residual),
    overdue: (item) => amountSort(item.overdue),
    lastPayment: (item) => item.lastPayment ?? null,
  });
  const total = (pick: (item: (typeof ofCycle)[number]) => string | null) =>
    fromCents(ofCycle.reduce((sum, item) => sum + toCents(pick(item) ?? '0'), 0));
  const year = list.data?.openingYear;

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Schede contabili</h1>
          <p className="muted">
            Clienti e fornitori con fatture o con un saldo al 1° gennaio: quanto è aperto e quanto è
            scaduto. Si apre la scheda per vedere fatture, pagamenti e mastrini.
          </p>
        </div>
      </div>
      <nav className="subtabs" aria-label="Ciclo">
        {(
          [
            ['passivo', 'Fornitori'],
            ['attivo', 'Clienti'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={cycle === key ? 'subtab selected' : 'subtab'}
            onClick={() => setCycle(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      {list.data && (
        <div className="treasury-tiles">
          {year && (
            <div className="treasury-tile">
              <span>Saldo al 01/01/{year}</span>
              <strong>{formatMoney(total((item) => item.opening))}</strong>
            </div>
          )}
          <div className="treasury-tile">
            <span>Aperto oggi</span>
            <strong>{formatMoney(total((item) => item.residual))}</strong>
          </div>
          <div className="treasury-tile">
            <span>di cui scaduto</span>
            <strong>{formatMoney(total((item) => item.overdue))}</strong>
          </div>
          <div className="treasury-tile">
            <span>Soggetti</span>
            <strong>{ofCycle.length}</strong>
          </div>
        </div>
      )}
      <div className="ledger-toolbar">
        <label className="search-box">
          <Icon name="search" />
          <input
            value={query}
            placeholder="Cerca il soggetto"
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <select
          className="compact-select"
          aria-label="Soggetti"
          value={onlyOpen ? 'open' : 'all'}
          onChange={(event) => setOnlyOpen(event.target.value === 'open')}
        >
          <option value="open">Solo con importi aperti</option>
          <option value="all">Tutti</option>
        </select>
        <select
          className="compact-select"
          aria-label="Ordina"
          value={sort}
          onChange={(event) => setSort(event.target.value as Sort)}
        >
          <option value="residual">Ordina per aperto</option>
          <option value="overdue">Ordina per scaduto</option>
          <option value="opening">Ordina per saldo iniziale</option>
          <option value="name">Ordina per nome</option>
        </select>
      </div>
      {list.isError && <p className="error">{list.error.message}</p>}
      {list.data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortHeader sort={sorting} column="name">
                  Soggetto
                </SortHeader>
                {year && (
                  <SortHeader sort={sorting} column="opening" numeric>
                    Saldo al 01/01/{year}
                  </SortHeader>
                )}
                <SortHeader sort={sorting} column="invoices" numeric>
                  Fatture
                </SortHeader>
                <SortHeader sort={sorting} column="residual" numeric>
                  Aperto oggi
                </SortHeader>
                <SortHeader sort={sorting} column="overdue" numeric>
                  Scaduto
                </SortHeader>
                <SortHeader sort={sorting} column="lastPayment">
                  Ultimo {cycle === 'attivo' ? 'incasso' : 'pagamento'}
                </SortHeader>
              </tr>
            </thead>
            <tbody>
              {sorting.rows.map((item) => (
                <tr key={`${item.subjectId}-${item.cycle}`}>
                  <td>
                    <Link to={`/tesoreria/schede-contabili/${item.subjectId}`}>{item.name}</Link>
                  </td>
                  {year && (
                    <td className="numeric">
                      {item.opening === null ? '—' : formatMoney(item.opening)}
                    </td>
                  )}
                  <td className="numeric">{item.invoices}</td>
                  <td className="numeric">
                    <strong>{formatMoney(item.residual)}</strong>
                  </td>
                  <td className="numeric">
                    {toCents(item.overdue) > 0 ? (
                      <span className="warn-text">{formatMoney(item.overdue)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{item.lastPayment ? formatDate(item.lastPayment) : '—'}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted">
                    Nessun soggetto con questi filtri.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** One account statement, from Tesoreria › Schede contabili. */
export function StatementPage() {
  const { id = '' } = useParams();
  const canRegistry = useCan('anagrafica.read');
  const statement = useQuery({
    queryKey: ['treasury', 'statements', id],
    queryFn: () => apiRequest<SubjectStatementResponse>(`/treasury/statements/${id}`),
  });
  usePageTitle(
    statement.data ? `Scheda contabile · ${statement.data.subject.name}` : 'Scheda contabile',
  );
  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/tesoreria/schede-contabili">← Schede contabili</Link>
          </p>
          <h1>{statement.data?.subject.name ?? 'Scheda contabile'}</h1>
          {canRegistry && (
            <p className="muted small">
              <Link to={`/anagrafica/${id}`}>Apri l’anagrafica</Link>
            </p>
          )}
        </div>
      </div>
      <StatementView subjectId={id} />
    </section>
  );
}
